/**
 * Service worker registration + "new version available" hook.
 *
 * Behaviour:
 *  • register /sw.js on every load
 *  • watch the registration for a `waiting` SW (= new build downloaded)
 *  • emit an `onUpdate` callback so the app can show a toast
 *  • on user accept → postMessage SKIP_WAITING → SW activates → reload
 */
export function registerServiceWorker({ onUpdate } = {}) {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator)
  ) {
    return;
  }
  // Only register on https or localhost — browsers refuse otherwise.
  const swUrl = '/sw.js';

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(swUrl);

      // If a new SW was found mid-session
      registration.addEventListener('updatefound', () => {
        const incoming = registration.installing;
        if (!incoming) return;
        incoming.addEventListener('statechange', () => {
          if (
            incoming.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            // New version sitting in waiting — controller exists means
            // a prior SW is currently in charge.
            onUpdate && onUpdate(registration);
          }
        });
      });

      // Edge case: a 'waiting' worker already existed when the page loaded
      if (registration.waiting && navigator.serviceWorker.controller) {
        onUpdate && onUpdate(registration);
      }

      // Periodically poll for new SW files (every 30 minutes). Keeps long-
      // open tabs honest without forcing a hard reload.
      setInterval(() => {
        registration.update().catch(() => {});
      }, 30 * 60 * 1000);

      // When the new SW takes control, do a clean reload so the page
      // matches the new JS bundle.
      let didReload = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (didReload) return;
        didReload = true;
        window.location.reload();
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('SW registration failed', e);
    }
  });
}

export function skipWaiting(registration) {
  if (registration && registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}
